'use client';

import type { SlackConnection } from '@/lib/nango/types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { integrationAuthStatusesRootQueryKey } from '@/data/integrations/use-integration-auth-statuses';
import { useNangoIntegration } from '@/data/nango/use-nango-integration';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getIntegrationConnectionState } from '@/lib/integrations/auth-status';
import { REALTIME_KEYS } from '@/lib/realtime/types';

export const slackQueryKey = ['slack-connection'];

export function useSlackIntegration() {
  const queryClient = useQueryClient();

  return useNangoIntegration<SlackConnection>({
    provider: 'slack',
    getConnectionUrl: '/api/slack/connections/get',
    disconnectUrl: '/api/slack/connections/delete',
    realtimeEventKey: REALTIME_KEYS.SLACK_CONNECTION_UPDATE,
    queryKey: slackQueryKey,
    syncAfterAuthUrl: '/api/slack/connections/sync',
    getConnectionState: getIntegrationConnectionState,
    callbacks: {
      onConnectAttempt: () => {
        trackClientEvent('slack_connection_attempt', undefined);
      },
      onConnectSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: integrationAuthStatusesRootQueryKey });
        toast.dismiss('slack-refresh-failure');
        toast.success('Slack account connected');
        trackClientEvent('slack_connection_successful', undefined, {
          team_id: data?.teamId,
          team_name: data?.teamName,
        });
      },
      onConnectError: (_error, reason) => {
        if (reason === 'popup_blocked') {
          toast.error(
            'Browser blocked the Slack authentication popup. Please allow popups for this site and try again.',
          );
          trackClientEvent('slack_connection_failed', undefined, { reason: 'popup_blocked' });
        } else {
          toast.error('Failed to connect Slack');
          trackClientEvent('slack_connection_failed', undefined, {
            reason: reason || 'auth_error',
          });
        }
      },
      onDisconnectSuccess: () => {
        queryClient.invalidateQueries({ queryKey: integrationAuthStatusesRootQueryKey });
        toast.success('Slack account disconnected');
      },
      onDisconnectError: () => {
        toast.error('Failed to disconnect Slack');
      },
      onPollingTimeout: (attempts) => {
        trackClientEvent('slack_connection_failed', undefined, {
          reason: 'polling_timeout',
          attempts,
        });
        toast.error('Failed to confirm Slack connection. Please try again.');
      },
    },
    onRealtimeEventAction: (payload, { queryClient, queryKey, setIsPolling }) => {
      if (payload?.type === 'slack_connection_update') {
        queryClient.setQueryData(
          queryKey,
          (payload.slackConnection ?? null) as SlackConnection | null,
        );
      } else {
        queryClient.invalidateQueries({ queryKey });
      }

      if (payload?.refreshFailure) {
        toast.error('Your Slack connection expired. Please reconnect.', {
          id: 'slack-refresh-failure',
        });
        trackClientEvent('slack_connection_failed', undefined, {
          reason: 'refresh_failure',
          connection_id: payload.refreshFailure.connectionId,
          error_type: payload.refreshFailure.errorType,
        });
        setIsPolling(false);
        return;
      }
      setIsPolling(false);
    },
  });
}
